import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CreateLeadInput {
  imobiliaria_id: string;
  nome: string;
  email?: string | null; // Email é opcional e pode ser null
  telefone: string;
  canal_entrada: 'site_imobiliaria' | 'portal' | 'whatsapp' | 'indicacao' | 'facebook' | 'instagram' | 'outro';
  tipo_negociacao: 'locacao' | 'venda';
  status?: 'novo_lead' | 'em_atendimento' | 'qualificado' | 'convertido' | 'perdido' | 'arquivado';
  perfil_interesse?: {
    tipo_imovel?: string[];
    finalidade?: string;
    quartos_min?: number;
    valor_max?: number;
    bairros?: string[];
    mensagem?: string;
    [key: string]: any;
  };
}

export interface CreateLeadWithImovelInput extends CreateLeadInput {
  imovel_id?: string; // Imóvel específico de interesse
}

/**
 * Hook para criar leads com interesse em imóvel usando Edge Function
 */
export function useCreateLeadInteresse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      nome: string;
      email?: string;
      telefone?: string;
      imovelId: string;
      imobiliariaId: string;
      tipoNegociacao: 'venda' | 'locacao';
      valorPrincipal: number;
      _hp_field?: string; // Honeypot field for bot detection
    }) => {
      console.log('📤 Chamando Edge Function criar-lead-interesse');

      const { data, error } = await supabase.functions.invoke('criar-lead-interesse', {
        body: {
          nome: input.nome,
          email: input.email || undefined,
          telefone: input.telefone || undefined,
          imovelId: input.imovelId,
          imobiliariaId: input.imobiliariaId,
          tipoNegociacao: input.tipoNegociacao,
          valorPrincipal: input.valorPrincipal,
          _hp_field: input._hp_field || '', // Include honeypot in request
        },
      });

      if (error) {
        console.error('❌ Erro ao chamar Edge Function:', error);
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Erro ao registrar interesse');
      }

      console.log('✅ Lead criado via Edge Function:', data.lead_id);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Hook para criar leads no HouseBit (método legado - usar useCreateLeadInteresse quando possível)
 */
export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateLeadWithImovelInput) => {
      const { imovel_id, ...restData } = input;

      const leadPayload = {
        nome: restData.nome,
        email: restData.email,
        telefone: restData.telefone,
        imobiliaria_id: restData.imobiliaria_id,
        canal_entrada: restData.canal_entrada,
        tipo_negociacao: restData.tipo_negociacao,
        status: restData.status || 'novo_lead',
        perfil_interesse: restData.perfil_interesse || {},
      };

      console.log('📤 Payload EXPLÍCITO do lead:', JSON.stringify(leadPayload, null, 2));

      const { data: lead, error: leadError } = await (supabase as any)
        .from('leads')
        .insert(leadPayload)
        .select()
        .single();

      if (leadError) {
        console.error('❌ Erro ao criar lead:', leadError);
        throw leadError;
      }
      if (!lead) throw new Error('Erro ao criar lead');
      
      console.log('✅ Lead criado:', lead.id);

      if (imovel_id) {
        const { error: interesseError } = await (supabase as any)
          .from('lead_imoveis_interesse')
          .insert({
            lead_id: lead.id,
            imovel_id: imovel_id,
            ordem: 1,
          });

        if (interesseError) throw interesseError;

        const { data: imovel } = await (supabase as any)
          .from('imoveis')
          .select('metricas_leads')
          .eq('id', imovel_id)
          .single();

        if (imovel) {
          await (supabase as any)
            .from('imoveis')
            .update({
              metricas_leads: (imovel.metricas_leads || 0) + 1,
            })
            .eq('id', imovel_id);
        }
      }

      return lead;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Hook para incrementar visualizações de um imóvel
 * @deprecated Client-side metric updates removed for security - metrics should be updated server-side only
 */
export function useIncrementVisualizacao() {
  return useMutation({
    mutationFn: async (_imovelId: string) => {
      // SECURITY: Client-side metric updates disabled to prevent manipulation
      // View tracking should be done server-side via edge functions or analytics
      console.log('ℹ️ View tracking disabled client-side for security');
      return;
    },
  });
}

/**
 * Hook para criar leads de proprietários (formulário Anuncie)
 * Envia dados para a HouseBit via Edge Function
 */
export function useCreateLeadProprietario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      nome: string;
      email?: string;
      telefone?: string;
      imobiliariaId: string;
      finalidade: 'alugar' | 'vender';
      cidade: string;
      bairro?: string;
      valor?: string;
      tipo: string;
      _hp_field?: string; // Honeypot field for bot detection
    }) => {
      console.log('📤 Enviando dados para HouseBit');

      // Chamar Edge Function da HouseBit (mesmo Supabase)
      const { data, error } = await supabase.functions.invoke('housebit-registrar-proprietario', {
        body: {
          nome: input.nome,
          email: input.email || undefined,
          telefone: input.telefone || undefined,
          imobiliariaId: input.imobiliariaId,
          finalidade: input.finalidade,
          cidade: input.cidade,
          bairro: input.bairro || undefined,
          valor: input.valor || undefined,
          tipo: input.tipo,
          origem: 'site_imobiliaria', // Identifica a origem
          _hp_field: input._hp_field || '', // Include honeypot in request
        },
      });

      if (error) {
        console.error('❌ Erro ao chamar HouseBit:', error);
        throw new Error('Erro ao conectar com HouseBit');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Erro ao registrar proprietário');
      }

      console.log('✅ Proprietário registrado na HouseBit:', data.proprietario_id);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

/**
 * Hook para registrar visita ao site
 * @deprecated Client-side metric updates removed for security - metrics should be updated server-side only
 */
export function useRegisterSiteVisit() {
  return useMutation({
    mutationFn: async (_siteId: string) => {
      // SECURITY: Client-side metric updates disabled to prevent manipulation
      // Site visit tracking should be done server-side via edge functions or analytics
      console.log('ℹ️ Site visit tracking disabled client-side for security');
      return;
    },
  });
}
